import type { Reporter } from "idfweb-tasks";
import { resolvePath, type Workbench } from "idfweb-workbench";
import { renderInline } from "idfweb-workbench/markdown";

import { resolveLines, type LineRange } from "./line-spec.ts";
import type { Layout } from "./project-state.ts";

import "./guide.css";

/**
 * The guide operations that `idfweb:` links in a README run: point at a
 * control of the page, open a file at lines with a note, run one of the
 * commands a click on the toolbar or the palette would run, set the layout,
 * and choose the project root, as a workshop's steps do. The links come from
 * READMEs anyone may write, so each runs only on
 * a click, and nothing here writes a file or flashes a board.
 */

/**
 * Commands a README may run; each is one click away in the toolbar or the
 * palette. Toggles are left out, since a click cannot tell which way it goes;
 * a `show` link opens what holds its target.
 */
const COMMANDS = new Set([
    "idfweb.build",
    "workbench.action.showCommands",
    "workbench.action.quickOpen",
    "workbench.action.gotoSymbol",
    "workbench.action.showAllSymbols",
    "workbench.action.findInFiles",
    "workbench.action.openSettings",
    "workbench.action.openGlobalKeybindings",
    "workbench.action.output.toggleOutput",
]);

/** The elements that have each role, explicit or implicit, for `idfweb:show?<role>=<name>`. */
const ROLES: Record<string, string> = {
    button: "button, [role=button]",
    link: "a[href], [role=link]",
    tab: "[role=tab]",
    combobox: "select, [role=combobox]",
    textbox: "input:not([type]), input[type=text], input[type=search], textarea, [role=textbox]",
    checkbox: "input[type=checkbox], [role=checkbox]",
    treeitem: "[role=treeitem]",
    menuitem: "[role=menuitem]",
    heading: "h1, h2, h3, h4, h5, h6, [role=heading]",
};

/**
 * Layouts a guide can set, by name: whether the left side bar and the
 * right-hand column show, and the output panel's tab.
 */
export const LAYOUTS: Record<string, Pick<Layout, "sidebar" | "side" | "tab">> = {
    /** The editor alone, such as for a README or a long file. */
    reading: { sidebar: false, side: false },
    /** The explorer and the editor. */
    editing: { sidebar: true, side: false },
    /** The editor and the serial monitor. */
    monitor: { sidebar: false, side: true, tab: "Serial Monitor" },
    /** The editor and the build output. */
    output: { sidebar: false, side: true, tab: "Build" },
    /** The explorer, the editor and the output panel. */
    all: { sidebar: true, side: true },
};

/**
 * The guide operations. `run` runs a README's link; the others are the same
 * operations for a caller the user chose, such as an agent's tool call, and
 * resolve to why they could not be done.
 */
export interface Guide {
    run(href: string, title: string | undefined, dir: string): void;
    /** Resolves to why not, or to how the spotlight closes. */
    show(role: string, name: string, text: string | undefined): Promise<string | { closed: Promise<Closed> }>;
    /** Resolves to the lines shown, or to why not. */
    open(path: string, lines: string, note: string): Promise<LineRange | string>;
    /** Point at the symbol `name` in the sdkconfig editor, by default the project root's sdkconfig. */
    setting(name: string, text: string | undefined, path?: string): Promise<string | { closed: Promise<Closed> }>;
    layout(preset: string, path: string | undefined): Promise<string | undefined>;
    /** The controls `show` can point at, one per line as `<role> "<name>"`. */
    controls(): string[];
}

export interface GuideOptions {
    /** Show the part of the page that holds `element`, such as the right-hand column, when it is hidden. */
    reveal(element: Element): void;
    /** Whether `reveal` can show `element`. */
    revealable(element: Element): boolean;
    /** The project root's sdkconfig. */
    sdkconfig(): string;
    /** Configure the project root unless a build would not, which creates its sdkconfig; resolves to why it failed. */
    configure(): Promise<string | undefined>;
    /** Show the side bars and the panel's tab as given, and store them as the layout. */
    setLayout(layout: Pick<Layout, "sidebar" | "side" | "tab">): void;
    /** Make the directory `path` the project root that builds use; resolves to an error string on failure, or undefined on success. */
    selectProject(path: string): Promise<string | undefined>;
}

export function createGuide(workbench: Workbench, reporter: Reporter, options: GuideOptions): Guide {
    const spotlight = new Spotlight();
    const show = async (role: string, name: string, text: string | undefined) => {
        if (!(role in ROLES)) return `The role "${role}" is not one of ${Object.keys(ROLES).join(", ")}.`;
        const target = await showTarget(role, name, options);
        if (target === undefined) return `The page has no ${role} "${name}".`;
        return { closed: spotlight.show(target, text) };
    };
    const open = async (path: string, lines: string, note: string) => {
        let range: LineRange | string = "";
        const problem = await workbench.showLines(path, (text) => (range = resolveLines(lines, text)), note);
        return problem ?? range;
    };
    const setting = async (name: string, text: string | undefined, path = options.sdkconfig()) => {
        const failed = await options.configure();
        if (failed !== undefined) return `The project could not be configured: ${failed}`;
        const row = await workbench.showSetting(path, name.replace(/^CONFIG_/, ""));
        if (typeof row === "string") return row;
        return { closed: spotlight.show(row, text) };
    };
    const layout = async (preset: string, path: string | undefined) => {
        const chosen = LAYOUTS[preset];
        if (chosen === undefined) return `The layout "${preset}" is not one of ${Object.keys(LAYOUTS).join(", ")}.`;
        options.setLayout(chosen);
        if (path !== undefined && !(await workbench.open(path))) return `${path} is not here.`;
        return undefined;
    };
    // A step of a workshop: the project to build next, with its README to read.
    const project = async (path: string, preset: string | undefined) => {
        const failed = await options.selectProject(path);
        if (failed !== undefined) return failed;
        if (preset !== undefined) {
            const problem = await layout(preset, undefined);
            if (problem !== undefined) return problem;
        }
        await workbench.open(`${path}/README.md`);
        return undefined;
    };
    const run = async (href: string, title: string | undefined, dir: string) => {
        const { action, params } = parseAction(href);
        let problem: string | { closed: unknown } | undefined;
        if (action === "show") {
            const [role, name] = params[0] ?? [];
            problem =
                role === undefined
                    ? `The link ${href} needs a role, one of ${Object.keys(ROLES).join(", ")}.`
                    : await show(role, name!, title);
        } else if (action === "open") {
            const path = param(params, "path");
            const shown =
                path === undefined
                    ? `The link ${href} needs a path.`
                    : await open(resolvePath(dir, path), param(params, "lines") ?? "1", title ?? "");
            if (typeof shown === "string") problem = shown;
        } else if (action === "command") {
            const id = param(params, "id") ?? "";
            if (!COMMANDS.has(id)) problem = `A README cannot run the command "${id}".`;
            else void workbench.execute(id);
        } else if (action === "config") {
            const name = param(params, "name");
            problem = name === undefined ? `The link ${href} needs a name.` : await setting(name, title);
        } else if (action === "project") {
            const path = param(params, "path");
            problem =
                path === undefined
                    ? `The link ${href} needs a path.`
                    : await project(resolvePath(dir, path), param(params, "preset"));
        } else if (action === "layout") {
            const path = param(params, "path");
            problem = await layout(
                param(params, "preset") ?? "",
                path === undefined ? undefined : resolvePath(dir, path),
            );
        } else {
            problem = `Unknown action in the link ${href}.`;
        }
        if (typeof problem === "string") reporter.message("warn", problem);
    };
    return {
        run: (href, title, dir) => void run(href, title, dir),
        show,
        open,
        setting,
        layout,
        controls: () => listControls(options),
    };
}

/** Whether Markdown links to a project of its tree with `idfweb:project`, as a workshop's outline does. */
export function linksProjects(markdown: string): boolean {
    return /\]\(<?idfweb:project\?/i.test(markdown);
}

/**
 * An `idfweb:` link's action and parameters. Values are percent-decoded only:
 * `+` stays a plus, as a line range's inclusive end needs, and a space is
 * `%20` or a space inside a `<...>` destination.
 */
export function parseAction(href: string): { action: string; params: [string, string][] } {
    const rest = href.replace(/^idfweb:/i, "");
    const query = rest.indexOf("?");
    const params =
        query < 0
            ? []
            : rest
                  .slice(query + 1)
                  .split("&")
                  .filter(Boolean)
                  .map((pair): [string, string] => {
                      const equals = pair.indexOf("=");
                      return equals < 0
                          ? [decoded(pair), ""]
                          : [decoded(pair.slice(0, equals)), decoded(pair.slice(equals + 1))];
                  });
    return { action: query < 0 ? rest : rest.slice(0, query), params };
}

function decoded(text: string): string {
    try {
        return decodeURIComponent(text);
    } catch {
        return text;
    }
}

function param(params: [string, string][], key: string): string | undefined {
    return params.find(([k]) => k === key)?.[1];
}

/**
 * The element to point at, made visible: the part of the page that holds it
 * is shown, and a tab is selected, so the spotlight lands on what the text
 * talks about.
 */
async function showTarget(role: string, name: string, options: GuideOptions): Promise<Element | undefined> {
    const target = findByRole(role, name);
    if (target === undefined) return undefined;
    if (!visible(target)) {
        options.reveal(target);
        await new Promise(requestAnimationFrame);
    }
    if (role === "tab" && target.getAttribute("aria-selected") !== "true") (target as HTMLElement).click();
    return visible(target) ? target : undefined;
}

function visible(element: Element): boolean {
    return element.getClientRects().length > 0;
}

/**
 * The element with the role whose accessible name is `name`, ignoring case,
 * or else starts with it, since a toolbar button's name ends with its key.
 * A visible one comes before a hidden one.
 */
export function findByRole(role: string, name: string): Element | undefined {
    const wanted = normalize(name);
    const candidates = Array.from(document.querySelectorAll(ROLES[role]!))
        // A README's own button often carries the name of the control it points at.
        .filter((element) => element.closest(".wb-markdown") === null)
        .map((element) => ({ element, name: normalize(accessibleName(element)) }))
        .sort((a, b) => Number(visible(b.element)) - Number(visible(a.element)));
    return (candidates.find((c) => c.name === wanted) ?? candidates.find((c) => c.name.startsWith(wanted)))?.element;
}

/**
 * The named controls of the page with a role `show` knows: the visible ones,
 * and the hidden ones that `show` first reveals. A README's own are left out.
 */
function listControls(options: GuideOptions): string[] {
    const lines = new Set<string>();
    for (const [role, selector] of Object.entries(ROLES)) {
        for (const element of Array.from(document.querySelectorAll(selector))) {
            if (element.closest(".wb-markdown, .guide-card") !== null) continue;
            if (!visible(element) && !options.revealable(element)) continue;
            const name = accessibleName(element).replace(/\s+/g, " ").trim();
            if (name !== "") lines.add(`${role} "${name.length > 80 ? `${name.slice(0, 80)}…` : name}"`);
        }
    }
    return [...lines];
}

function normalize(text: string): string {
    return text.replace(/\s+/g, " ").trim().toLowerCase();
}

/** The accessible name, as far as the page's own controls need: aria-label, aria-labelledby, a label, the text. */
function accessibleName(element: Element): string {
    const label = element.getAttribute("aria-label");
    if (label !== null) return label;
    const labelledBy = element.getAttribute("aria-labelledby");
    if (labelledBy !== null) {
        return labelledBy
            .split(/\s+/)
            .map((id) => document.getElementById(id)?.textContent ?? "")
            .join(" ");
    }
    if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
    ) {
        const text = Array.from(element.labels ?? [])
            .map((l) => l.textContent ?? "")
            .join(" ");
        return text !== "" ? text : (element.getAttribute("placeholder") ?? element.title);
    }
    const text = element.textContent ?? "";
    return text.trim() !== "" ? text : ((element as HTMLElement).title ?? "");
}

/**
 * A dimmed page with a hole around one element and a card next to it. It
 * follows the element as the layout moves it, and closes when the element is
 * clicked, on Escape, on a click elsewhere, or from the card's button.
 */
class Spotlight {
    #close: ((how: Closed) => void) | undefined;

    /** Resolves to how the spotlight closed. */
    show(target: Element, text: string | undefined): Promise<Closed> {
        this.#close?.("replaced");
        let closed: (how: Closed) => void = () => {};
        const result = new Promise<Closed>((resolve) => (closed = resolve));
        const hole = document.createElement("div");
        hole.className = "guide-hole";
        const card = document.createElement("div");
        card.className = "guide-card";
        card.setAttribute("role", "status");
        if (text !== undefined) {
            const body = document.createElement("div");
            body.append(renderInline(text));
            card.append(body);
        }
        const done = document.createElement("button");
        done.type = "button";
        done.textContent = "Got It";
        card.append(done);
        document.body.append(hole, card);

        let frame = 0;
        const place = () => {
            const box = target.getBoundingClientRect();
            if (!target.isConnected || box.width === 0) {
                close("gone");
                return;
            }
            const pad = 4;
            Object.assign(hole.style, {
                left: `${box.left - pad}px`,
                top: `${box.top - pad}px`,
                width: `${box.width + 2 * pad}px`,
                height: `${box.height + 2 * pad}px`,
            });
            const gap = 12;
            const below = box.bottom + gap + card.offsetHeight <= window.innerHeight;
            const top = below ? box.bottom + gap : Math.max(8, box.top - gap - card.offsetHeight);
            const left = Math.min(
                Math.max(8, box.left + box.width / 2 - card.offsetWidth / 2),
                window.innerWidth - card.offsetWidth - 8,
            );
            card.style.top = `${top}px`;
            card.style.left = `${left}px`;
            frame = requestAnimationFrame(place);
        };
        const onPointer = (event: PointerEvent) => {
            if (event.target instanceof Node && card.contains(event.target)) return;
            // A click on the target closes the spotlight and does what the target does.
            close(event.target instanceof Node && target.contains(event.target) ? "target" : "elsewhere");
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") close("escape");
        };
        const close = (how: Closed) => {
            closed(how);
            cancelAnimationFrame(frame);
            hole.remove();
            card.remove();
            window.removeEventListener("pointerdown", onPointer, true);
            window.removeEventListener("keydown", onKey, true);
            if (this.#close === close) this.#close = undefined;
        };
        done.addEventListener("click", () => close("button"));
        window.addEventListener("pointerdown", onPointer, true);
        window.addEventListener("keydown", onKey, true);
        this.#close = close;
        target.scrollIntoView({ block: "nearest", inline: "nearest" });
        place();
        done.focus({ preventScroll: true });
        return result;
    }
}

/** How a spotlight closed: its control or the card's button clicked, Escape, a click elsewhere, the next spotlight, or its control gone. */
export type Closed = "target" | "button" | "escape" | "elsewhere" | "replaced" | "gone";
